import * as Dialog from '@radix-ui/react-dialog';
import { Command } from 'cmdk';
import { Fragment, isValidElement, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { type CardOriginChannel, type CardSearchResult } from '@voll-intelligence/domain';

import { t } from '@web/i18n';
import { queryKeys } from '@web/core/query';
import { AppIcons, Button } from '@web/ui/core/atoms';
import { ChannelChip, CustomerAvatar, channelIcon } from '@web/ui/core/molecules';
// Leaf import (not the organisms barrel): the barrel re-exports profile_menu /
// card_claim_banner, and pulling those into this app-wide palette triggers a
// partial-init cycle under the web suite's isolate:false module sharing.
import { SegmentedButton } from '@web/ui/core/organisms/segmented_button';
import { fireAndForget, twcn } from '@web/ui/core/utils';
import { useDebouncedValue, useOrgContext } from '@web/ui/core';

import {
  CONNECTED_CHANNELS,
  DATE_PRESETS,
  EMPTY_FILTERS,
  channelSummary,
  formatRelativeDate,
  type CardSearchFilters,
} from './card_filters';

import { projectsRepository, useCommandPalette } from '@web/data/repositories';
import type { CustomerHit } from '@web/data/repositories/search/customer_search_repository';
import { useSearch, type SearchState } from './use_search';
import { useCustomerSearch, type CustomerSearchState } from './use_customer_search';

/** The result kind the list is scoped to, or `null` for the grouped (all) view. */
type Page = 'card' | 'customer' | null;
const SCOPE_ORDER: readonly Page[] = [null, 'card', 'customer'];

/** Source data carries the literal "unknown" for missing names; treat it as absent. */
const PLACEHOLDER = 'unknown';

/** Trims, then drops empty and "unknown" placeholder entries — kills the `unknown · unknown` meta. */
function cleanParts(...parts: readonly (string | null | undefined)[]): string[] {
  return parts
    .map((p) => p?.trim() ?? '')
    .filter((p) => p !== '' && p.toLowerCase() !== PLACEHOLDER);
}

/** The contact line that matched the query (email or phone), so the consultant sees why a hit surfaced. */
function matchedContact(hit: CustomerHit, query: string): string {
  const q = query.trim().toLowerCase();
  if (hit.email?.toLowerCase().includes(q)) return hit.email;
  if (hit.phone?.toLowerCase().includes(q)) return hit.phone;
  return hit.email ?? hit.phone ?? '';
}

/** Best-effort content signature for a meta-line part, keyed alongside its index — parts are a
 * short mix of strings and elements with no field that's independently unique across siblings. */
function partSignature(part: React.ReactNode): string {
  if (typeof part === 'string') return part;
  if (typeof part === 'number') return String(part);
  if (isValidElement(part) && part.key != null) return part.key;
  return 'node';
}

/** Muted middot-separated meta line; each string becomes its own span so it truncates and is queryable. */
function MetaLine({ parts }: Readonly<{ parts: readonly React.ReactNode[] }>): React.JSX.Element {
  const visible = parts.filter((p) => p !== null && p !== undefined && p !== '');
  return (
    <div className="mt-0.5 flex min-w-0 items-center gap-1.5 text-meta text-muted-foreground">
      {visible.map((part, i) => (
        <Fragment key={`${String(i)}-${partSignature(part)}`}>
          {i > 0 && <span aria-hidden>·</span>}
          {typeof part === 'string' ? <span className="truncate">{part}</span> : part}
        </Fragment>
      ))}
    </div>
  );
}

/** Clickable group label + count. Clicking scopes the list to this kind. */
function GroupHeader({
  label,
  count,
  onScope,
}: Readonly<{ label: string; count: number; onScope: () => void }>): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onScope}
      className="sticky top-0 flex w-full items-center gap-2 bg-card px-4 py-1.5 text-left"
    >
      <span className="text-tiny font-medium uppercase text-muted-foreground">{label}</span>
      <span className="rounded-sm bg-tone-gray-tint px-1.5 text-tiny tabular-nums text-tone-gray-ink">
        {count}
      </span>
    </button>
  );
}

/** The `project › squad` line for a card, or `Triagem` for a squad-less card. */
function cardLocation(
  r: CardSearchResult,
  projectNames: ReadonlyMap<string, string>,
  squadNames: ReadonlyMap<string, string>,
): string | null {
  const parts = [
    r.projectId ? projectNames.get(r.projectId) : undefined,
    r.squadId ? squadNames.get(r.squadId) : undefined,
  ].filter(Boolean);
  if (parts.length > 0) return parts.join(' › ');
  return r.projectId === null && r.squadId === null ? t('search.card.triageFallback') : null;
}

const ROW = twcn(
  'group flex w-full cursor-pointer items-center gap-3 rounded-sm px-4 py-2.5 outline-none',
  'aria-selected:bg-primary-tint',
);
const ROW_TITLE = 'truncate text-base font-medium leading-card-title';
const ENTER_HINT =
  'hidden flex-shrink-0 text-tiny text-muted-foreground group-aria-selected:inline';

function CardRow({
  result,
  projectNames,
  squadNames,
  onSelect,
}: Readonly<{
  result: CardSearchResult;
  projectNames: ReadonlyMap<string, string>;
  squadNames: ReadonlyMap<string, string>;
  onSelect: (r: CardSearchResult) => void;
}>): React.JSX.Element {
  const title = result.subject ?? result.customerName ?? `#${String(result.seq)}`;
  return (
    <Command.Item
      value={result.id}
      onSelect={() => {
        onSelect(result);
      }}
      className={ROW}
    >
      <span className="flex w-6 flex-shrink-0 justify-center">
        <AppIcons
          name="ticket"
          size={15}
          className="text-muted-foreground group-aria-selected:text-primary"
        />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className={ROW_TITLE}>{title}</span>
          {result.isClosed && (
            <span className="flex-shrink-0 rounded-sm bg-tone-gray-tint px-1.5 py-0.5 text-tiny font-medium text-tone-gray-ink">
              {t('search.card.closed')}
            </span>
          )}
          <span className="ml-auto flex flex-shrink-0 items-center gap-2 text-tiny text-muted-foreground">
            <span>{formatRelativeDate(result.createdAt)}</span>
            <span className="font-mono tabular-nums">#{result.seq}</span>
            <span className="hidden group-aria-selected:inline">↵</span>
          </span>
        </div>
        <MetaLine
          parts={[
            <ChannelChip key="channel" channel={result.originChannel} />,
            ...cleanParts(result.customerName, result.companyName),
            cardLocation(result, projectNames, squadNames),
          ]}
        />
      </div>
    </Command.Item>
  );
}

function CustomerRow({
  hit,
  query,
  onSelect,
}: Readonly<{
  hit: CustomerHit;
  query: string;
  onSelect: (hit: CustomerHit) => void;
}>): React.JSX.Element {
  return (
    <Command.Item
      value={hit.id}
      onSelect={() => {
        onSelect(hit);
      }}
      className={ROW}
    >
      <span className="flex w-6 flex-shrink-0 justify-center">
        <CustomerAvatar name={hit.name} vip={hit.vip} size="sm" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className={ROW_TITLE}>{hit.name}</span>
          <span className={twcn('ml-auto', ENTER_HINT)}>↵</span>
        </div>
        <MetaLine parts={cleanParts(matchedContact(hit, query), hit.companyName)} />
      </div>
    </Command.Item>
  );
}

/** Removable filter pill shown in the input row while the list is scoped to one kind. */
function ScopePill({
  page,
  onClear,
}: Readonly<{ page: 'card' | 'customer'; onClear: () => void }>): React.JSX.Element {
  const label = t(page === 'card' ? 'search.scope.cards' : 'search.scope.customers');
  return (
    <button
      type="button"
      onClick={onClear}
      aria-label={t('search.filter.removeAria', { label })}
      className="inline-flex flex-shrink-0 items-center gap-1 rounded-sm bg-primary-tint px-2 py-0.5 text-meta text-primary"
    >
      <AppIcons name={page === 'card' ? 'ticket' : 'users'} size={12} />
      <span>{label}</span>
      <AppIcons name="close" size={12} />
    </button>
  );
}

/** Channel multi-select, single-select period segment, and an active-filter summary. Cards only. */
function FilterBar({
  filters,
  onChange,
}: Readonly<{
  filters: CardSearchFilters;
  onChange: (next: CardSearchFilters) => void;
}>): React.JSX.Element {
  const allActive = filters.channels.length === 0;

  function toggleChannel(ch: CardOriginChannel): void {
    const has = filters.channels.includes(ch);
    onChange({
      ...filters,
      channels: has ? filters.channels.filter((c) => c !== ch) : [...filters.channels, ch],
    });
  }
  function clearChannels(): void {
    onChange({ ...filters, channels: [] });
  }

  const groupLabel =
    'w-16 shrink-0 pt-1.5 font-mono text-tiny uppercase tracking-widest text-faint-foreground';
  const chipBase =
    'inline-flex items-center gap-1.5 rounded-pill border px-2.5 py-1 text-base transition-colors duration-base';
  const chipOff =
    'border-border bg-surface text-foreground-default hover:border-border-strong hover:bg-muted';
  const chipOn = 'border-primary bg-primary-tint font-medium text-primary-ink';
  const periodText = DATE_PRESETS.find((p) => p.key === filters.period)?.label ?? '';

  return (
    <div className="border-b border-border">
      {/* Canal — multisseleção. "Todos" é o estado sem filtro de canal. */}
      <div className="flex gap-4 px-4 py-3.5">
        <span className={groupLabel}>{t('search.filter.channel')}</span>
        <div className="flex flex-1 flex-wrap gap-1.5">
          <button
            type="button"
            aria-pressed={allActive}
            onClick={clearChannels}
            className={twcn(chipBase, allActive ? chipOn : chipOff)}
          >
            <AppIcons
              name={allActive ? 'check-square' : 'square'}
              size={15}
              className={allActive ? 'text-primary' : 'text-muted-foreground'}
            />
            {t('search.filter.allChannels')}
          </button>
          {CONNECTED_CHANNELS.map((ch) => {
            const selected = filters.channels.includes(ch);
            return (
              <button
                key={ch}
                type="button"
                aria-label={t('search.filter.channelAria', { channel: ch })}
                aria-pressed={selected}
                onClick={() => {
                  toggleChannel(ch);
                }}
                className={twcn(chipBase, 'capitalize', selected ? chipOn : chipOff)}
              >
                <AppIcons
                  name={channelIcon(ch)}
                  size={15}
                  className={selected ? 'text-primary' : 'text-muted-foreground'}
                />
                {ch}
                {selected && <AppIcons name="check" size={13} className="text-primary" />}
              </button>
            );
          })}
        </div>
      </div>
      {/* Período — seleção única */}
      <div className="flex gap-4 border-t border-border-soft px-4 py-3.5">
        <span className={groupLabel}>{t('search.filter.period')}</span>
        <SegmentedButton ariaLabel={t('search.filter.periodAria')}>
          {DATE_PRESETS.map((p) => (
            <SegmentedButton.Item
              key={p.key}
              label={p.label}
              isActive={filters.period === p.key}
              onClick={() => {
                onChange({ ...filters, period: p.key });
              }}
            />
          ))}
        </SegmentedButton>
      </div>

      {/* Resumo do filtro ativo + limpar canais */}
      <div className="flex items-center gap-3.5 border-t border-border-soft bg-muted px-4 py-2.5">
        <span className="text-meta text-muted-foreground">
          {channelSummary(filters.channels)} ·{' '}
          <span className="font-medium text-foreground">{periodText}</span>
        </span>
        <span className="flex-1" />
        <Button variant="ghost" size="sm" onClick={clearChannels} disabled={allActive}>
          <AppIcons name="close" size={13} />
          {t('search.filter.clearChannels')}
        </Button>
      </div>
    </div>
  );
}

const STATUS = 'px-4 py-6 text-center text-meta text-muted-foreground';

/** Type-to-search / loading / no-results placeholder; `null` once results render. */
function PaletteStatus({
  isActive,
  isFetching,
  isEmpty,
}: Readonly<{
  isActive: boolean;
  isFetching: boolean;
  isEmpty: boolean;
}>): React.JSX.Element | null {
  if (!isActive)
    return <Command.Empty className={STATUS}>{t('search.empty.typeToSearch')}</Command.Empty>;
  if (isFetching) return <div className={STATUS}>{t('search.searching')}</div>;
  if (isEmpty)
    return <Command.Empty className={STATUS}>{t('search.empty.noResults')}</Command.Empty>;
  return null;
}

/** The scrolling results list: status states, the two groups, and the separator between them. */
function PaletteResults({
  cards,
  customers,
  page,
  query,
  projectNames,
  squadNames,
  onScope,
  onSelectCard,
  onSelectCustomer,
}: Readonly<{
  cards: SearchState;
  customers: CustomerSearchState;
  page: Page;
  query: string;
  projectNames: ReadonlyMap<string, string>;
  squadNames: ReadonlyMap<string, string>;
  onScope: (page: 'card' | 'customer') => void;
  onSelectCard: (r: CardSearchResult) => void;
  onSelectCustomer: (hit: CustomerHit) => void;
}>): React.JSX.Element {
  const showCards = page !== 'customer' && cards.results.length > 0;
  const showCustomers = page !== 'card' && customers.results.length > 0;
  const visibleCount =
    (showCards ? cards.results.length : 0) + (showCustomers ? customers.results.length : 0);

  return (
    <Command.List className="max-h-96 overflow-y-auto py-1">
      <PaletteStatus
        isActive={cards.isActive || customers.isActive}
        isFetching={cards.isFetching || customers.isFetching}
        isEmpty={visibleCount === 0}
      />

      {showCards && (
        <Command.Group>
          {page === null && (
            <GroupHeader
              label={t(query.trim().length >= 2 ? 'search.group.cards' : 'search.group.recent')}
              count={cards.results.length}
              onScope={() => {
                onScope('card');
              }}
            />
          )}
          {cards.results.map((r) => (
            <CardRow
              key={r.id}
              result={r}
              projectNames={projectNames}
              squadNames={squadNames}
              onSelect={onSelectCard}
            />
          ))}
        </Command.Group>
      )}

      {showCards && showCustomers && (
        <Command.Separator alwaysRender className="my-1 h-px bg-border" />
      )}

      {showCustomers && (
        <Command.Group>
          {page === null && (
            <GroupHeader
              label={t('search.group.customers')}
              count={customers.results.length}
              onScope={() => {
                onScope('customer');
              }}
            />
          )}
          {customers.results.map((hit) => (
            <CustomerRow key={hit.id} hit={hit} query={query} onSelect={onSelectCustomer} />
          ))}
        </Command.Group>
      )}
    </Command.List>
  );
}

export function CommandPalette(): React.JSX.Element {
  const { open, setOpen } = useCommandPalette();
  const { orgId, orgSlug } = useOrgContext();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [page, setPage] = useState<Page>(null);
  const [filters, setFilters] = useState<CardSearchFilters>(EMPTY_FILTERS);
  const debounced = useDebouncedValue(query, 250);
  const cards = useSearch(debounced, filters, open);
  const customers = useCustomerSearch(debounced);

  // Project/squad names for each result's location line. Same query the org
  // sidebar uses (deduped by TanStack Query), so it's already warm here.
  const projectsQuery = useQuery({
    queryKey: queryKeys.projects.list(orgId),
    queryFn: () => projectsRepository.loadProjects(orgId),
  });
  const { projectNames, squadNames } = useMemo(() => {
    const projectNames = new Map<string, string>();
    const squadNames = new Map<string, string>();
    for (const p of projectsQuery.data ?? []) {
      projectNames.set(p.id, p.name);
      for (const s of p.squads) squadNames.set(s.id, s.name);
    }
    return { projectNames, squadNames };
  }, [projectsQuery.data]);

  function close(): void {
    setOpen(false);
    setQuery('');
    setPage(null);
    setFilters(EMPTY_FILTERS);
  }

  function cycleScope(direction: 1 | -1): void {
    setPage((cur) => {
      const next = (SCOPE_ORDER.indexOf(cur) + direction + SCOPE_ORDER.length) % SCOPE_ORDER.length;
      return SCOPE_ORDER[next] ?? null;
    });
  }

  // Tab cycles the type scope (cmdk "pages"); stop the event so Radix's focus
  // trap doesn't steal it. Backspace on an empty query exits the scope.
  function onInputKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'Tab') {
      e.preventDefault();
      e.stopPropagation();
      cycleScope(e.shiftKey ? -1 : 1);
      return;
    }
    if (e.key === 'Backspace' && query === '') setPage(null);
  }

  function openCard(result: CardSearchResult): void {
    close();
    if (result.squadId === null) {
      const to = '/orgs/$orgSlug/triage/card/$cardId';
      fireAndForget(navigate({ to, params: { orgSlug, cardId: result.id } }));
      return;
    }
    const params = { orgSlug, squadId: result.squadId, cardId: result.id, tab: 'conversation' };
    fireAndForget(navigate({ to: '/orgs/$orgSlug/squad/$squadId/card/$cardId/$tab', params }));
  }

  function openCustomer(hit: CustomerHit): void {
    close();
    const to = '/orgs/$orgSlug/customers/$id';
    fireAndForget(navigate({ to, params: { orgSlug, id: hit.id } }));
  }

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(isOpen) => {
        if (isOpen) setOpen(true);
        else close();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay
          className={twcn(
            'fixed inset-0 z-scrim bg-black/40',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
          )}
        />
        <Dialog.Content
          aria-describedby={undefined}
          className={twcn(
            'fixed left-1/2 top-24 z-drawer w-full max-w-prose-2xl -translate-x-1/2',
            'flex flex-col overflow-hidden rounded-lg border border-border bg-card shadow-overlay outline-none',
            'data-[state=open]:animate-in data-[state=closed]:animate-out duration-200',
            'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
            'data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95',
          )}
        >
          <Dialog.Title className="sr-only">{t('search.dialogTitle')}</Dialog.Title>

          {/* cmdk shouldFilter=false: results are server-driven by useSearch */}
          <Command shouldFilter={false}>
            {/* Input row */}
            <div className="flex items-center gap-2 border-b border-border px-4 py-3">
              <AppIcons
                name="magnifying-glass"
                size={16}
                className="flex-shrink-0 text-muted-foreground"
              />
              {page && (
                <ScopePill
                  page={page}
                  onClear={() => {
                    setPage(null);
                  }}
                />
              )}
              <Command.Input
                value={query}
                onValueChange={setQuery}
                onKeyDown={onInputKeyDown}
                placeholder={t('search.inputPlaceholder')}
                className="flex-1 bg-transparent text-base outline-none placeholder:text-muted-foreground"
              />
            </div>

            {page !== 'customer' && <FilterBar filters={filters} onChange={setFilters} />}

            <PaletteResults
              cards={cards}
              customers={customers}
              page={page}
              query={query}
              projectNames={projectNames}
              squadNames={squadNames}
              onScope={setPage}
              onSelectCard={openCard}
              onSelectCustomer={openCustomer}
            />

            {/* Footer legend */}
            <div className="flex items-center gap-4 border-t border-border px-4 py-2 text-tiny text-muted-foreground">
              <span>{t('search.legend.navigate')}</span>
              <span>{t('search.legend.open')}</span>
              <span>{t('search.legend.filter')}</span>
              <span>{t('search.legend.close')}</span>
            </div>
          </Command>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
